import * as THREE from 'three';
import { PORTAL_HALF_W, PORTAL_HALF_H } from '../portals/Portal.js';

/**
 * Multiplayer racing, client side. Two pieces: RaceClient speaks the room
 * protocol over a WebSocket (join, 12Hz state, start, finish), and
 * RemotePlayers renders everyone else as interpolated replicas: no
 * physics, no colliders, a body and a name. The same trust model as the
 * leaderboard: clients are honest because their ghosts are watchable.
 */

const SEND_HZ = 12;

/** The quick-ping vocabulary; keys are the wire, labels are the screen. */
export const PINGS = { gg: 'GG', gl: 'GOOD LUCK', wow: 'WOW!!', go: "LET'S GO" };
const _vp = new THREE.Vector3();

/**
 * Start-line slot for racer #index: a 4-wide grid behind the spawn point.
 * Everyone computes the same slots from the same sorted roster, so nobody
 * spawns inside anybody (the reported white-out was a teammate's visor at
 * point-blank range).
 */
export function startOffset(index) {
  const col = index % 4;
  const row = Math.floor(index / 4);
  return { dx: (col - 1.5) * 1.7, dz: row * 1.8 };
}

export class RaceClient {
  constructor({ code, name, on }) {
    this.code = code;
    this.on = on;
    this.id = null;
    this.hostId = null;
    this.open = false;
    this._acc = 0;

    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.ws = new WebSocket(`${proto}://${location.host}/api/room/${code}`);
    this.ws.addEventListener('open', () => {
      this.open = true;
      this._everOpen = true;
      this._send({ t: 'join', name });
    });
    this.ws.addEventListener('message', (e) => {
      let m;
      try { m = JSON.parse(e.data); } catch { return; }
      if (m.t === 'hello') { this.id = m.id; this.hostId = m.host; }
      if (m.t === 'roster') this.hostId = m.host;
      this.on?.(m);
    });
    this.ws.addEventListener('close', () => {
      const early = !this._everOpen;
      this.open = false;
      this.on?.({ t: 'closed', early });
    });
    this.ws.addEventListener('error', () => {
      const early = !this._everOpen;
      this.open = false;
      this.on?.({ t: 'closed', early });
    });
  }

  get isHost() { return this.id !== null && this.id === this.hostId; }

  _send(obj) {
    if (this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(obj)); } catch { /* closing */ }
    }
  }

  /** Call every frame while connected; throttles itself to SEND_HZ. */
  tick(dt, player, levelId = '') {
    if (!this.open) return;
    this._acc += dt;
    if (this._acc < 1 / SEND_HZ) return;
    this._acc = 0;
    const p = player.position;
    const v = player.controller.velocity;
    this._send({
      t: 's',
      l: levelId,
      p: [+p.x.toFixed(2), +p.y.toFixed(2), +p.z.toFixed(2)],
      y: +player.yaw.toFixed(3),
      x: +player.pitch.toFixed(3),
      v: [+v.x.toFixed(1), +v.y.toFixed(1), +v.z.toFixed(1)],
    });
  }

  sendStart() { this._send({ t: 'start' }); }
  sendPing(k) { this._send({ t: 'ping', k }); }
  sendRename(name) { this._send({ t: 'rename', name }); }
  sendRematch() { this._send({ t: 'start', rematch: true }); }
  sendVote(level) { this._send({ t: 'vote', level }); }

  sendPortal(which, levelId, pos, quat) {
    this._send({
      t: 'p', w: which, l: levelId,
      p: [+pos.x.toFixed(2), +pos.y.toFixed(2), +pos.z.toFixed(2)],
      q: [+quat.x.toFixed(3), +quat.y.toFixed(3), +quat.z.toFixed(3), +quat.w.toFixed(3)],
    });
  }

  sendPortalClear() { this._send({ t: 'pc' }); }
  sendFinish(time) { this._send({ t: 'fin', time: +time.toFixed(2) }); }

  dispose() {
    try { this.ws.close(); } catch { /* already closed */ }
    this.open = false;
  }
}

/** The other racers: colored replicas with name tags, lerped between updates. */
export class RemotePlayers {
  constructor(scene) {
    this.scene = scene;
    this.map = new Map();
  }

  _tag(name) {
    const c = document.createElement('canvas');
    const x = c.getContext('2d');
    x.font = '700 28px ui-monospace, monospace';
    const w = Math.ceil(x.measureText(name).width) + 24;
    c.width = w; c.height = 44;
    x.font = '700 28px ui-monospace, monospace';
    x.fillStyle = 'rgba(5,6,12,0.55)';
    x.fillRect(0, 0, w, 44);
    x.fillStyle = '#bfe9ff';
    x.textBaseline = 'middle';
    x.fillText(name, 12, 24);
    const tex = new THREE.CanvasTexture(c);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex, transparent: true, depthWrite: false,
    }));
    sprite.scale.set(w / 44, 1, 1);
    return sprite;
  }

  _colorFor(id) {
    let h = 0;
    for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return new THREE.Color().setHSL((h % 360) / 360, 0.85, 0.6);
  }

  /** A rim-only replica of a peer's portal: zero render-target cost. */
  _rim(color) {
    const group = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1, 0.06, 8, 40),
      new THREE.MeshBasicMaterial({ color, toneMapped: false }),
    );
    ring.scale.set(PORTAL_HALF_W, PORTAL_HALF_H, 1);
    group.add(ring);
    const fill = new THREE.Mesh(
      new THREE.CircleGeometry(1, 32),
      new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0.14,
        side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
      }),
    );
    fill.scale.set(PORTAL_HALF_W, PORTAL_HALF_H, 1);
    group.add(fill);
    group.visible = false;
    this.scene.add(group);
    return group;
  }

  setPortal(id, which, p, q) {
    const e = this.map.get(id) ?? this.ensure(id, '???');
    const rim = e.rims[which] ?? (e.rims[which] = this._rim(this._colorFor(id)));
    rim.position.set(p[0], p[1], p[2]);
    rim.quaternion.set(q[0], q[1], q[2], q[3]);
    rim.visible = true;
  }

  clearPortals(id) {
    const e = this.map.get(id);
    if (!e) return;
    for (const rim of Object.values(e.rims)) rim.visible = false;
  }

  ensure(id, name) {
    if (this.map.has(id)) return this.map.get(id);
    const color = this._colorFor(id);
    const group = new THREE.Group();
    // Readable against ANY backdrop: additive holograms wash out to nothing
    // in front of lit panels (reported as "they went into a portal and
    // vanished"), so the body is solid color with a dark outline shell.
    const outline = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.5, 0.96, 6, 16),
      new THREE.MeshBasicMaterial({ color: 0x05060c, side: THREE.BackSide, toneMapped: false }),
    );
    outline.position.y = 0.9;
    group.add(outline);
    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.42, 0.96, 6, 16),
      new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0.92, toneMapped: false,
      }),
    );
    body.position.y = 0.9;
    group.add(body);
    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.34, 0.09, 0.06),
      new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }),
    );
    visor.position.set(0, 1.52, -0.42);
    group.add(visor);
    const tag = this._tag(name || '???');
    tag.position.y = 2.35;
    group.add(tag);
    group.visible = false;
    this.scene.add(group);
    const entry = {
      group, name: name || '???', rims: {},
      target: new THREE.Vector3(), vel: new THREE.Vector3(),
      yaw: 0, pitch: 0, pitchS: 0, seen: false, shown: false,
    };
    this.map.set(id, entry);
    return entry;
  }

  setName(id, name) {
    const e = this.map.get(id);
    if (!e) return;
    // Rebuild the whole replica: names change once, at join.
    this.remove(id);
    this.ensure(id, name);
  }

  setState(id, p, yaw, v, pitch) {
    const e = this.map.get(id) ?? this.ensure(id, '???');
    e.target.set(p[0], p[1], p[2]);
    if (Array.isArray(v)) e.vel.set(v[0], v[1], v[2]);
    e.yaw = yaw ?? 0;
    e.pitch = pitch ?? 0;
    if (!e.seen) {
      e.group.position.copy(e.target);
      e.seen = true;
    }
    e.shown = true;
  }

  update(dt, viewpoint = null) {
    for (const e of this.map.values()) {
      if (!e.seen) continue;
      // Rendered = logically present AND not filling the camera: a replica
      // inside your near plane is a screen-sized white-out, not a racer.
      // Measured eye to BODY CENTRE: within 1.2m you are inside them;
      // a start-grid neighbour 1.7m away stays visible.
      if (viewpoint) {
        _vp.copy(e.group.position);
        _vp.y += 0.9;
        e.group.visible = e.shown && _vp.distanceToSquared(viewpoint) > 1.44;
      } else {
        e.group.visible = e.shown;
      }
      // Dead-reckon a touch ahead with the reported velocity, then chase:
      // smooth at 12Hz updates, and a portal-scale jump just snaps.
      const d = e.group.position.distanceTo(e.target);
      // Anything past a stride is a portal: snap. BOOT's walk-through hop
      // is ~20m and smearing a teleport across the chasm reads as a glitch.
      if (d > 12) e.group.position.copy(e.target);
      else {
        e.target.addScaledVector(e.vel, dt * 0.5);
        e.group.position.lerp(e.target, Math.min(1, dt * 10));
      }
      e.group.rotation.y += (e.yaw - e.group.rotation.y) * Math.min(1, dt * 10);
      // Pitch never bends the replica (a capsule tilting reads as falling
      // over); the smoothed value exists for the spectator's POV camera.
      e.pitchS += (e.pitch - e.pitchS) * Math.min(1, dt * 10);
    }
  }

  /** Out of this world (literally): keep the entry, hide body and rims. */
  hide(id) {
    const e = this.map.get(id);
    if (!e) return;
    e.shown = false;
    e.group.visible = false;
    for (const rim of Object.values(e.rims)) rim.visible = false;
  }

  hideAll() {
    for (const id of this.map.keys()) this.hide(id);
  }

  remove(id) {
    const e = this.map.get(id);
    if (!e) return;
    for (const rim of Object.values(e.rims)) {
      rim.removeFromParent();
      rim.traverse((o) => { if (o.isMesh) { o.geometry?.dispose?.(); o.material?.dispose?.(); } });
    }
    e.group.removeFromParent();
    e.group.traverse((o) => {
      if (o.isMesh || o.isSprite) { o.geometry?.dispose?.(); o.material?.map?.dispose?.(); o.material?.dispose?.(); }
    });
    this.map.delete(id);
  }

  clear() {
    for (const id of [...this.map.keys()]) this.remove(id);
  }
}
